import React from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';

/**
 * Boundary global para errores de render. Envolverlo alrededor de <App />
 * en src/index.js. Reporta el error a Sentry y muestra una UI de fallback.
 *
 * IMPORTANTE: no envolver <Provider> ni <Router> con este boundary —
 * si esos tronan en mount, el boundary no tiene contexto Redux/Router
 * para renderizar el fallback. Envolver solo <App />.
 *
 * Fallback UI deliberadamente minimal (sin Material-UI, sin styled-components,
 * sin Redux): si el árbol de render se rompe arriba, los deps de UI también
 * pueden estar rotos. Inline styles para garantizar render bajo cualquier
 * estado.
 */
function FallbackUI({ resetError }) {
  const handleReload = () => {
    // Hard reload — limpia state corrupto y intenta de nuevo. La llamada a
    // window.location.reload() puede tirar en runtimes no-browser (notablemente
    // JSDOM usado en tests: "Not implemented: navigation"). En ese caso el
    // click handler erraría y el usuario quedaría sin recuperación. Lo
    // envolvemos en try/catch y caemos a resetError() para garantizar que
    // siempre haya UNA vía de recuperación.
    if (typeof window !== 'undefined' && window.location && typeof window.location.reload === 'function') {
      try {
        window.location.reload();
        return;
      } catch (_e) {
        // fallthrough al resetError
      }
    }
    resetError();
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        padding: '24px',
        textAlign: 'center',
      }}
      role="alert"
      aria-live="assertive"
    >
      <h1 style={{ fontSize: '24px', marginBottom: '8px' }}>
        Algo salió mal
      </h1>
      <p style={{ color: '#666', marginBottom: '24px', maxWidth: '420px' }}>
        Ocurrió un error inesperado. Ya lo reportamos a nuestro equipo. Por favor,
        recarga la página para continuar.
      </p>
      <button
        type="button"
        onClick={handleReload}
        style={{
          padding: '10px 20px',
          fontSize: '14px',
          border: 'none',
          borderRadius: '4px',
          background: '#0066cc',
          color: '#fff',
          cursor: 'pointer',
        }}
      >
        Recargar
      </button>
    </div>
  );
}

FallbackUI.propTypes = {
  resetError: PropTypes.func.isRequired,
};

/**
 * Wrapper de Sentry.ErrorBoundary con fallback UI estándar.
 *
 * Usage en src/index.js:
 *   import { SentryErrorBoundary } from './observability/ErrorBoundary';
 *   ReactDOM.render(
 *     <Provider store={store}>
 *       <SentryErrorBoundary>
 *         <App />
 *       </SentryErrorBoundary>
 *     </Provider>,
 *     document.getElementById('root'),
 *   );
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Subtree a proteger (idealmente solo <App />)
 */
export function SentryErrorBoundary({ children }) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => <FallbackUI resetError={resetError} />}
      showDialog={false}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

SentryErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

export default SentryErrorBoundary;
